import { Controller } from "web-ui/controller"
import { getCSRFToken } from "web-ui/csrf"
import * as Log from "web-ui/log"

// Connects to data-controller="option-list"
export default class extends Controller {
  static targets = [
    "search",
    "option",
    "checkbox",
    "group",
    "noSearchResults",
    "clearButton",
    "scrollIndicator"
  ]
  static values = { url: String }

  static events = {
    "input [data-option-list-target~='search']": "onSearchInput",
    "keydown [data-option-list-target~='search']": "onSearchKeyDown",
    "mousedown [data-option-list-target~='option']": "onOptionMouseDown",
    "click [data-option-list-target~='option']": "onOptionClick",
    "mousemove [data-option-list-target~='option']": "onOptionMouseMove",
    "change [data-option-list-target~='checkbox']": "onCheckboxChange",
    "click [data-option-list-target~='clearButton']": "clearSearch"
  }

  connect() {
    this.debug("Connect")

    this.activeIndex = null
    this.setActive(0)

    this.focusSearch()
  }

  disconnect() {
    this.debug("Disconnect")
  }

  onOptionMouseDown(event) {
    // Cancel focus shift so the search input remains focused - Matt, Taylor, Tue May 19 2026
    event.preventDefault()
  }

  onOptionClick(event, option) {
    const checkbox = option.querySelector('input[type="checkbox"]')

    // Let the default handler toggle and dispatch change - Matt, Taylor, Tue May 19 2026
    if (event.target === checkbox) {
      return
    }

    // Cancel label activation (toggles checkbox and focuses it); change event is dispatched manually - Matt, Taylor, Tue May 19 2026
    event.preventDefault()

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }

  onOptionMouseMove(event, option) {
    const index = this.visibleOptions().indexOf(option)

    if (index === -1) {
      return
    }

    this.setActive(index, false)
  }

  onCheckboxChange(event, checkbox) {
    const method = checkbox.checked ? "POST" : "DELETE"

    this.submit(checkbox.value, method)
  }

  onSearchInput(event, search) {
    this.applyFilter(search.value)
    this.setActive(0)
  }

  onSearchKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveActive(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveActive(-1)
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.toggleActiveCheckbox()
    }
  }

  focusSearch() {
    setTimeout(() => {
      this.searchTarget.focus()
    }, 0)
  }

  clearSearch() {
    if (!this.hasSearchTarget) {
      return
    }

    this.searchTarget.value = ""
    this.applyFilter("")
    this.setActive(0)
    this.focusSearch()
  }

  applyFilter(term) {
    const searchSegments = term.toLowerCase().split(/\s+/).filter((segment) => segment !== "")

    this.optionTargets.forEach((option) => {
      const optionText = option.textContent.toLowerCase()

      if (searchSegments.every((segment) => optionText.includes(segment))) {
        option.hidden = false
      } else {
        option.hidden = true
      }
    })

    this.groupTargets.forEach((group) => {
      const visibleOptions = group.querySelectorAll('[data-option-list-target~="option"]:not([hidden])')
      group.hidden = visibleOptions.length === 0
    })

    this.noSearchResultsTarget.hidden = this.visibleOptions().length !== 0

    this.scrollIndicatorTarget.displayIndicators()
  }

  moveActive(delta) {
    const visible = this.visibleOptions()

    if (visible.length === 0) {
      return
    }

    const nextIndex = Math.max(0, Math.min(visible.length - 1, this.activeIndex + delta))

    this.setActive(nextIndex)
  }

  setActive(index, scroll = true) {
    this.optionTargets.forEach((option) => option.classList.remove("active"))

    const visible = this.visibleOptions()

    if (visible.length === 0) {
      this.activeIndex = null
      return
    }

    this.activeIndex = index

    const active = visible[index]

    if (active) {
      active.classList.add("active")

      if (scroll) {
        active.scrollIntoView({ block: "nearest" })
      }
    }
  }

  visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden)
  }

  toggleActiveCheckbox() {
    if (this.activeIndex == null) {
      return
    }

    const visible = this.visibleOptions()
    const option = visible[this.activeIndex]

    if (!option) {
      return
    }

    const checkbox = option.querySelector('input[type="checkbox"]')

    checkbox.checked = !checkbox.checked
    checkbox.dispatchEvent(new Event("change", { bubbles: true }))
  }

  async submit(value, method) {
    this.debug("Submit", { value, method })

    const params = new URLSearchParams()
    params.set("value", value)

    this.element.setAttribute("aria-busy", "true")

    try {
      const response = await fetch(this.urlValue, {
        method,
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": getCSRFToken(),
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      })

      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      this.element.removeAttribute("aria-busy")
    }
  }

  debug(message, data) {
    Log.debug("Option List - " + message, data)
  }
}
